// Phrase library for share/motivation messages per decile and language
// Keys are deciles (0, 10, ..., 100). Each value is an array of 3 variants.

export const SHARE_PHRASES = {
  fr: {
    0: [
      "Un nouveau départ, tout reste à construire 🚀",
      "Chaque aventure commence par une première étape ✨",
      "Le point zéro, la page blanche pleine de possibilités 📖",
    ],
    10: [
      "Les premières briques sont posées 🧱",
      "Un début prometteur qui ouvre la voie 💡",
      "Une base fragile mais pleine d’avenir 🌱",
    ],
    20: [
      "Les fondations commencent à se dessiner 🔍",
      "Une étape clé vers l’alignement parfait 🎯",
      "Un socle sur lequel bâtir plus haut 🏗️",
    ],
    30: [
      "Un potentiel déjà bien présent 💡",
      "Un CV qui commence à parler le langage du poste 📊",
      "Les correspondances se renforcent petit à petit ⚡",
    ],
    40: [
      "Presque à mi-chemin, le cap est bien tracé 🚀",
      "Une vraie avancée vers le match parfait 🔑",
      "Les efforts se ressentent, la progression est claire 📈",
    ],
    50: [
      "Un équilibre solide, le cœur des compétences est là 💼",
      "La moitié du chemin parcouru, et ça se voit 👌",
      "Un socle fort déjà validé, place aux ajustements ✨",
    ],
    60: [
      "Le CV commence vraiment à s’aligner avec le poste 💡",
      "Les points forts ressortent clairement 🔍",
      "Un vrai pas vers la réussite, solide et motivant 🚀",
    ],
    70: [
      "Une belle correspondance déjà validée 💼",
      "Un score solide qui reflète un profil aligné ✨",
      "Une vraie compatibilité, encourageante et motivante 🔥",
    ],
    80: [
      "Une forte adéquation, le poste correspond parfaitement 👌",
      "Un alignement qui inspire confiance 💡",
      "Un CV qui colle déjà à la réalité du poste 💼",
    ],
    90: [
      "Quasiment le match idéal 🎯",
      "Un alignement remarquable 🚀",
      "Le profil se fond presque parfaitement dans le poste 💡",
    ],
    100: [
      "Match parfait, le CV et le poste s’accordent naturellement ✨",
      "Un alignement total, aucune limite 🚀",
      "Le poste et le profil se complètent parfaitement 💼",
    ],
  },
  en: {
    0: [
      "A fresh start, everything is yet to be built 🚀",
      "Every journey begins with a first step ✨",
      "Ground zero, a blank page full of possibilities 📖",
    ],
    10: [
      "The first bricks are laid 🧱",
      "A promising start that opens the way 💡",
      "A fragile base but full of potential 🌱",
    ],
    20: [
      "The foundations are starting to take shape 🔍",
      "A key step toward perfect alignment 🎯",
      "A base to build higher upon 🏗️",
    ],
    30: [
      "Clear potential already emerging 💡",
      "A CV that begins to speak the job’s language 📊",
      "The matches are gradually strengthening ⚡",
    ],
    40: [
      "Almost halfway, the path is well defined 🚀",
      "A real step forward toward the perfect match 🔑",
      "Progress is visible, efforts are paying off 📈",
    ],
    50: [
      "A solid balance, the core skills are here 💼",
      "Halfway there, and it shows 👌",
      "A strong foundation already validated, time for fine-tuning ✨",
    ],
    60: [
      "The CV is really starting to align with the role 💡",
      "Strengths are standing out clearly 🔍",
      "A solid step toward success, motivating and clear 🚀",
    ],
    70: [
      "A strong correspondence already validated 💼",
      "A solid score reflecting an aligned profile ✨",
      "Real compatibility, motivating and inspiring 🔥",
    ],
    80: [
      "A strong match, the role fits perfectly 👌",
      "An alignment that inspires confidence 💡",
      "A CV already closely matching the job reality 💼",
    ],
    90: [
      "Almost the ideal match 🎯",
      "Remarkable alignment 🚀",
      "The profile nearly blends perfectly into the role 💡",
    ],
    100: [
      "Perfect match, the CV and the role align naturally ✨",
      "Total alignment, no limits 🚀",
      "The role and the profile complement each other flawlessly 💼",
    ],
  },
};

const NORMALIZED_LANGUAGE_KEYS = {
  fr: "fr",
  fr_fr: "fr",
  fr_ca: "fr",
  fr_be: "fr",
  fr_ch: "fr",
  french: "fr",
  en: "en",
  en_us: "en",
  en_gb: "en",
  en_ca: "en",
  english: "en",
};

const clampScore = (score: number) => {
  const numeric = Number.isFinite(score)
    ? score
    : Number.parseFloat(score.toString());
  if (Number.isNaN(numeric)) return 0;
  if (numeric < 0) return 0;
  if (numeric > 100) return 100;
  return numeric;
};

const getDecileBucketFromScore = (score: number) => {
  const clamped = clampScore(score);
  const roundedToNearestTen = Math.round(clamped / 10) * 10;
  return roundedToNearestTen;
};

const resolveLanguageKey = (language: string) => {
  const fallback = "fr";
  if (!language) return fallback;
  const normalized = String(language).trim().toLowerCase().replace(/[-]/g, "_");
  return (
    NORMALIZED_LANGUAGE_KEYS[
      normalized as keyof typeof NORMALIZED_LANGUAGE_KEYS
    ] || fallback
  );
};

export const getMotivationalSharePhrase = (language: string, score: number) => {
  const langKey = resolveLanguageKey(language);
  const decile = getDecileBucketFromScore(score);
  const phrasesForDecile =
    SHARE_PHRASES[langKey as keyof typeof SHARE_PHRASES]?.[
      decile as keyof typeof SHARE_PHRASES.fr
    ] || SHARE_PHRASES.fr[decile as keyof typeof SHARE_PHRASES.fr];
  const randomIndex = Math.floor(Math.random() * phrasesForDecile.length);
  return phrasesForDecile[randomIndex];
};

export const getPhrasesForStep = (language: string, step: number) => {
  const langKey = resolveLanguageKey(language);
  const decile = getDecileBucketFromScore(step);
  return (
    SHARE_PHRASES[langKey as keyof typeof SHARE_PHRASES]?.[
      decile as keyof typeof SHARE_PHRASES.fr
    ] ||
    SHARE_PHRASES.fr[decile as keyof typeof SHARE_PHRASES.fr] ||
    []
  );
};
